import React, { useState, useEffect, useRef, useMemo } from 'react';
import emailjs from 'emailjs-com';
import {
  Button,
  Table,
  Modal,
  Select,
  Group,
  Title,
  Stack,
  Portal,
  Checkbox,
  Loader,
  Center,
  Text,
  TextInput,
  Flex,
} from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IconX,
  IconEdit,
  IconEye,
  IconSearch,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import { getRoles, getUsers, updateUser, deleteUser } from '../../services/accessManagement';

type Role = {
  id: string;
  name: string;
};

type User = {
  id: string;
  name: string;
  email: string;
  roles: string[];
  created_at?: string;
  updated_at?: string;
};

type SortField = 'name' | 'email' | 'role' | 'created_at';
type SortDirection = 'asc' | 'desc';

export default function AccessManagementPage() {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [deletingUserIds, setDeletingUserIds] = useState<string[]>([]);

  // Search, sort, and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [roleFilter, setRoleFilter] = useState<string>('');

  // States for editing a specific user's role
  const [editRolesModalOpened, setEditRolesModalOpened] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserRole, setEditingUserRole] = useState<string | null>(null);
  const [sendEmailChecked, setSendEmailChecked] = useState(false);

  // States for deleting a user
  const [deleteUserModalOpened, setDeleteUserModalOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for custom hover tooltip
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, label: '' });
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  // ==================== Filtered and Sorted Users ====================
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)
      );
    }

    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter((user) => user.roles.includes(roleFilter));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          if (aName < bName) comparison = -1;
          else if (aName > bName) comparison = 1;
          break;
        case 'email':
          const aEmail = a.email.toLowerCase();
          const bEmail = b.email.toLowerCase();
          if (aEmail < bEmail) comparison = -1;
          else if (aEmail > bEmail) comparison = 1;
          break;
        case 'role':
          const aRole = (a.roles[0] || '').toLowerCase();
          const bRole = (b.roles[0] || '').toLowerCase();
          if (aRole < bRole) comparison = -1;
          else if (aRole > bRole) comparison = 1;
          break;
        case 'created_at':
          const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
          comparison = aCreated - bCreated;
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [users, searchQuery, roleFilter, sortField, sortDirection]);

  // ==================== Sorting Handler ====================
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ==================== Sort Icon Component ====================
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconChevronDown size={14} style={{ marginLeft: 4 }} />
    );
  };

  // ==================== Fetch data from backend ====================
  const fetchRolesFromBackend = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const fetchedRoles = await getRoles(accessToken);
      if (fetchedRoles && Array.isArray(fetchedRoles)) {
        setRoles(fetchedRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUsersFromBackend = async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const fetchedUsers = await getUsers(accessToken);
      if (fetchedUsers && Array.isArray(fetchedUsers)) {
        setUsers(
          fetchedUsers.map((u: any) => ({
            id: u.user_id,
            name: u.name ?? u.nickname ?? 'No name',
            email: u.email,
            roles: u.roles || [],
            created_at: u.created_at,
            updated_at: u.updated_at,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ==================== Lifecycle: load data ====================
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchRolesFromBackend(), fetchUsersFromBackend()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ==================== Users: Update Roles ====================
  const handleUpdateUserRoles = async (userId: string, updatedRole: string | null) => {
    const roleIds = roles.filter((r) => r.name === updatedRole).map((r) => r.id);
    const roleNames = updatedRole ? [updatedRole] : [];
    try {
      const accessToken = await getAccessTokenSilently();
      await updateUser(accessToken, userId, roleIds, roleNames);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, roles: updatedRole ? [updatedRole] : [] } : user
        )
      );
    } catch (error) {
      console.error('Error updating user roles:', error);
    }
  };

  // ==================== Open Edit Roles Modal for a User ====================
  const openEditRolesModal = (userId: string, currentRoles: string[]) => {
    setEditingUserId(userId);
    setEditingUserRole(currentRoles[0] || null);
    setEditRolesModalOpened(true);
  };

  // ==================== Send Email ====================
  const sendEmail = async (toEmail: string) => {
    const templateParams = {
      email: toEmail,
    };
    try {
      await emailjs.send(
        'service_c9nwiz7',
        'template_rwi9b9a',
        templateParams,
        '5hhpj9_T6jQS1-Qq5'
      );
      console.log('Email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
    }
  };

  // ==================== Assign Role to a Specific User ====================
  const handleAssignRoles = async () => {
    if (!editingUserId) return;
    await handleUpdateUserRoles(editingUserId, editingUserRole);

    // If checkbox is checked, send email
    if (sendEmailChecked) {
      const user = users.find((u) => u.id === editingUserId);
      if (user && user.email) {
        await sendEmail(user.email);
      } else {
        console.error('User email not found');
      }
    }
    setEditRolesModalOpened(false);
    setEditingUserId(null);
    setEditingUserRole(null);
  };

  // ==================== Open Delete User Modal ====================
  const openDeleteUserModal = (user: User) => {
    setSelectedUser(user);
    setDeleteUserModalOpened(true);
  };

  // ==================== Delete User Handler ====================
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setDeletingUserIds((prev) => [...prev, selectedUser.id]);
    try {
      const accessToken = await getAccessTokenSilently();
      await deleteUser(accessToken, selectedUser.id);
      setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
      setDeleteUserModalOpened(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeletingUserIds((prev) => prev.filter((id) => id !== selectedUser.id));
    }
  };

  // ==================== Mouse Event Handlers for Custom Tooltip ====================
  const handleRowMouseEnter = (e: React.MouseEvent, userId: string) => {
    hoverTimeout.current = setTimeout(() => {
      setTooltip({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        label: t('editUserRole'),
      });
    }, 2000);
  };

  const handleRowMouseMove = (e: React.MouseEvent) => {
    if (tooltip.visible) {
      setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }));
    }
  };

  const handleRowMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setTooltip({ visible: false, x: 0, y: 0, label: '' });
  };

  if (loading) {
    return (
      <Stack align="stretch" style={{ padding: '2rem' }} w="60%" miw="500px">
        <Title order={2} mb="lg">
          {t('userManagement')}
        </Title>
        <Center style={{ height: '30vh' }}>
          <Loader size="lg" />
        </Center>
      </Stack>
    );
  }

  return (
    <Stack align="stretch" style={{ padding: '2rem' }} w="60%" miw="500px">
      <Title order={2} mb="lg">
        {t('userManagement')}
      </Title>

      <Flex justify="space-between" align="center" mb="md">
        <TextInput
          placeholder={t('searchUsers')}
          leftSection={<IconSearch size={16} />}
          style={{ flex: 1, marginRight: '10px' }}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
        />
        <Select
          data={[
            { value: '', label: t('allRoles') },
            ...roles.map((role) => ({
              value: role.name,
              label: role.name,
            })),
          ]}
          placeholder={t('filterByRole')}
          value={roleFilter}
          onChange={(value) => setRoleFilter(value || '')}
          style={{ width: '200px' }}
        />
      </Flex>

      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
              {t('name')} <SortIcon field="name" />
            </Table.Th>
            <Table.Th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
              {t('email')} <SortIcon field="email" />
            </Table.Th>
            <Table.Th onClick={() => handleSort('role')} style={{ cursor: 'pointer' }}>
              {t('role')} <SortIcon field="role" />
            </Table.Th>
            <Table.Th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
              {t('createdAt')} <SortIcon field="created_at" />
            </Table.Th>
            <Table.Th ta="center"></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredAndSortedUsers.length > 0 ? (
            filteredAndSortedUsers.map((user) => (
              <Table.Tr
                key={user.id}
                onMouseEnter={(e) => handleRowMouseEnter(e, user.id)}
                onMouseMove={handleRowMouseMove}
                onMouseLeave={handleRowMouseLeave}
                onClick={() => openEditRolesModal(user.id, user.roles)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{user.name}</Table.Td>
                <Table.Td>{user.email}</Table.Td>
                <Table.Td>{user.roles[0] || ''}</Table.Td>
                <Table.Td>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                </Table.Td>
                <Table.Td ta="center">
                  <Button
                    color="red"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteUserModal(user);
                    }}
                    loading={deletingUserIds.includes(user.id)}
                  >
                    {t('delete')}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))
          ) : (
            <Table.Tr>
              <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                <Text c="dimmed">{t('noUsersFound')}</Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {/* Portal for Custom Tooltip */}
      {tooltip.visible && (
        <Portal>
          <div
            style={{
              position: 'fixed',
              left: tooltip.x + 10,
              top: tooltip.y + 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            {tooltip.label}
          </div>
        </Portal>
      )}

      {/* Modal: Edit Role for a Specific User */}
      <Modal
        opened={editRolesModalOpened}
        onClose={() => setEditRolesModalOpened(false)}
        title={t('editUserRole')}
      >
        <Select
          data={roles.map((role) => ({
            value: role.name,
            label: role.name,
          }))}
          label={t('selectRole')}
          placeholder={t('selectRole')}
          value={editingUserRole}
          onChange={setEditingUserRole}
          mb="sm"
        />
        <Checkbox
          label={t('sendEmailNotification')}
          checked={sendEmailChecked}
          onChange={(event) => setSendEmailChecked(event.currentTarget.checked)}
          mb="sm"
        />
        <Group justify="space-around" mt="md">
          <Button variant="outline" onClick={() => setEditRolesModalOpened(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={handleAssignRoles}>{t('assign')}</Button>
        </Group>
      </Modal>

      {/* Modal: Confirm User Deletion */}
      <Modal
        opened={deleteUserModalOpened}
        onClose={() => setDeleteUserModalOpened(false)}
        title={t('confirmUserDeletion')}
      >
        <div>{t('confirmDeleteMessage', { name: selectedUser?.name })}</div>
        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={() => setDeleteUserModalOpened(false)}>
            {t('cancel')}
          </Button>
          <Button
            color="red"
            onClick={handleDeleteUser}
            loading={selectedUser ? deletingUserIds.includes(selectedUser.id) : false}
          >
            {t('delete')}
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
